/**
 * Answer a second call WITHOUT the Voice SDK hanging up the first.
 *
 * The SDK allows one call at a time. Accepting an offer runs a `beforeAccept`
 * hook that the `Device` sets on every call it builds, and that hook
 * disconnects whatever call is active. For call waiting that is exactly wrong:
 * the first customer is on server hold in a conference room, and the browser's
 * leg in that room is what keeps the room - and the customer - alive. Both
 * bridges give the operator's leg `endConferenceOnExit`, so the SDK hanging it
 * up would end the held customer's call too.
 *
 * So the hook is taken off the ONE call being answered as a knock, and only
 * there. `beforeAccept` is a private option of `Call` (SDK 2.x, checked against
 * 2.18.3); `keepOtherCalls.test.ts` reads the installed SDK's source so an
 * upgrade that moves it fails a test instead of a customer.
 *
 * Fail-safe by design: when the field is not where it is expected, this
 * returns false and the caller must NOT accept. The worst case is then a new
 * call that cannot be answered as a knock - never a held customer dropped.
 */

interface WithOptions {
  _options?: { beforeAccept?: unknown } | null;
}

/**
 * Remove the SDK's "hang up the active call first" step from this call.
 *
 * True when it is gone, false when the SDK's shape is not what this build
 * knows and accepting would be a gamble. A MISSING field is false too: a
 * renamed hook would still hang the first call up, and "not found" must not
 * read as "nothing to remove".
 */
export function keepOtherCallsOnAccept(call: unknown): boolean {
  if (!call || typeof call !== "object") return false;
  const options = (call as WithOptions)._options;
  if (!options || typeof options !== "object") return false;
  if (typeof options.beforeAccept !== "function") return false;
  options.beforeAccept = undefined;
  return true;
}
